import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ExternalLink, Link2, Trash2 } from 'lucide-react'
import { api } from '../../../lib/api'
import { Card } from '../../ui/Card'
import { useConfirmare } from '../../ui/Confirmare'
import { eroare, zi } from './comun'

/**
 * Graficile activității: nume + link către cloud.
 *
 * Nu se urcă fișiere — grafica trăiește în cloud și se corectează acolo. O copie în CRM s-ar
 * învechi de la prima corectură, iar linkul duce mereu la ultima versiune.
 *
 * Din 15.10.2026 (PR & Evenimente, etapa 2c): adăugarea se face direct pe pagină — lipești
 * linkul, scrii numele, Enter —, nu într-o fereastră. Sursa (Drive, Canva…) se recunoaște din
 * link și se vede pe rând.
 */

interface Grafica {
  id: number
  nume: string
  link: string
  adaugatDe: string
  createdAt: string
}

/** De unde e linkul, dupa domeniu. Doar o eticheta — linkul ramane cum l-a pus omul. */
const SURSE: [RegExp, string][] = [
  [/(^|\.)drive\.google\.com$|(^|\.)docs\.google\.com$/, 'Google Drive'],
  [/(^|\.)canva\.com$|(^|\.)canva\.link$/, 'Canva'],
  [/(^|\.)dropbox\.com$|(^|\.)db\.tt$/, 'Dropbox'],
  [/(^|\.)wetransfer\.com$|(^|\.)we\.tl$/, 'WeTransfer'],
  [/(^|\.)onedrive\.live\.com$|(^|\.)1drv\.ms$|sharepoint\.com$/, 'OneDrive'],
  [/(^|\.)figma\.com$/, 'Figma'],
  [/(^|\.)adobe\.com$|(^|\.)adobe\.ly$/, 'Adobe'],
]

export function sursaLink(link: string): string | null {
  try {
    const gazda = new URL(link).hostname.toLowerCase()
    for (const [re, nume] of SURSE) if (re.test(gazda)) return nume
    return gazda.replace(/^www\./, '')
  } catch {
    return null
  }
}

/** Ca serverul: doar http(s), altfel `javascript:` ar rula la clic. */
function linkValid(v: string): boolean {
  try {
    const u = new URL(v.trim())
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

export function FilaGrafice({ campanieId, canWrite }: { campanieId: number; canWrite: boolean }) {
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const cheie = ['campanie', campanieId, 'grafice']
  const [nume, setNume] = useState('')
  const [link, setLink] = useState('')

  const q = useQuery<{ grafice: Grafica[] }>({
    queryKey: cheie,
    queryFn: async () => (await api.get(`/marketing/grafice/campanie/${campanieId}`)).data,
  })

  const adauga = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/marketing/grafice/campanie/${campanieId}`, {
          nume: nume.trim(),
          link: link.trim(),
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cheie })
      toast.success('Grafica a fost adăugată.')
      setNume('')
      setLink('')
    },
    onError: eroare,
  })

  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/marketing/grafice/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: cheie }),
    onError: () => toast.error('Grafica nu s-a putut șterge.'),
  })

  const grafice = q.data?.grafice ?? []
  const lipit = link.trim()
  const bun = linkValid(lipit)
  const sursa = bun ? sursaLink(lipit) : null
  const dublura = bun ? grafice.find((g) => g.link.trim() === lipit) : undefined

  function trimite(e: FormEvent) {
    e.preventDefault()
    if (!bun || !nume.trim() || adauga.isPending) return
    adauga.mutate()
  }

  return (
    <Card
      title="Grafică"
      description="Linkuri din cloud — fișierele rămân în Drive, Canva, Dropbox…"
    >
      <div className="grid gap-3">
        {canWrite && (
          <form onSubmit={trimite} className="grid gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex min-w-0 flex-[2_1_260px] items-center gap-2 rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-b4l-text-dim focus-within:border-b4l-blue">
                <Link2 size={14} aria-hidden />
                <input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="Lipește linkul (Drive, Canva, Dropbox, WeTransfer…)"
                  aria-label="Link din cloud"
                  inputMode="url"
                  className="w-full bg-transparent text-corp text-b4l-text outline-none"
                />
              </label>
              <input
                value={nume}
                onChange={(e) => setNume(e.target.value)}
                placeholder="Nume (ex.: Roll-up 85×200)"
                aria-label="Numele graficii"
                maxLength={200}
                className="min-w-0 flex-[1_1_180px] rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-corp"
              />
              <button
                type="submit"
                disabled={!bun || !nume.trim() || adauga.isPending}
                className="rounded-caseta bg-b4l-blue px-3 py-1.5 text-corp font-semibold text-white hover:opacity-90 disabled:opacity-40"
              >
                {adauga.isPending ? 'Se salvează…' : 'Adaugă'}
              </button>
            </div>
            {lipit && (
              <p className={`text-mic ${bun ? 'text-b4l-text-dim' : 'text-b4l-red-dark'}`}>
                {!bun
                  ? 'Nu e un link valid — trebuie să înceapă cu http:// sau https://.'
                  : dublura
                    ? `Linkul e deja pus, ca „${dublura.nume}”.`
                    : `Sursa: ${sursa}${nume.trim() ? '' : ' · scrie și un nume'}`}
              </p>
            )}
          </form>
        )}

        {q.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : q.isError ? (
          <p className="text-sm text-b4l-red-dark">
            Graficile nu s-au putut încărca.{' '}
            <button type="button" onClick={() => void q.refetch()} className="underline">
              Reîncearcă
            </button>
          </p>
        ) : grafice.length === 0 ? (
          <p className="text-sm text-b4l-text-muted">Nicio grafică adăugată.</p>
        ) : (
          <ul className="divide-y divide-b4l-border text-sm">
            {grafice.map((g) => (
              <li key={g.id} className="flex items-center gap-3 py-2">
                <a
                  href={g.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 flex-1 items-center gap-2 text-b4l-blue-dark hover:underline"
                >
                  <ExternalLink size={14} className="shrink-0" aria-hidden />
                  <span className="truncate">{g.nume}</span>
                </a>
                {sursaLink(g.link) && (
                  <span className="hidden rounded-full bg-b4l-bg px-2 py-px text-[11px] text-b4l-text-muted sm:inline">
                    {sursaLink(g.link)}
                  </span>
                )}
                <span className="hidden text-xs text-b4l-text-muted md:inline">{g.adaugatDe}</span>
                <span className="w-20 text-right text-xs text-b4l-text-muted">
                  {zi(g.createdAt)}
                </span>
                {canWrite && (
                  <button
                    type="button"
                    title="Șterge"
                    aria-label={`Șterge grafica ${g.nume}`}
                    className="rounded p-1 text-b4l-text-muted hover:text-b4l-red-dark"
                    disabled={sterge.isPending}
                    onClick={async () => {
                      if (
                        await confirma({
                          titlu: `Ștergi „${g.nume}”?`,
                          mesaj: 'Se șterge doar linkul de aici. Fișierul din cloud rămâne.',
                          buton: 'Șterge',
                          pericol: true,
                        })
                      )
                        sterge.mutate(g.id)
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}
